import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from "recharts";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";

import img from "../../Assets/Images/close-up-portrait-young-bearded-man-white-shirt-jacket-posing-camera-with-broad-smile-isolated-gray_171337-629.jpg";
import SelectInput from "../InputComponents/SelectInput";

const Dashboard = () => {
  const [year1, setYear1] = useState("2025");
  const [year2, setYear2] = useState("2025");

  const jobData = [
    { month: "Jan", newCandidates: 40, deployedCandidates: 20 },
    { month: "Feb", newCandidates: 50, deployedCandidates: 25 },
    { month: "Mar", newCandidates: 45, deployedCandidates: 30 },
    { month: "Apr", newCandidates: 60, deployedCandidates: 35 },
    { month: "May", newCandidates: 70, deployedCandidates: 40 },
    { month: "Jun", newCandidates: 65, deployedCandidates: 38 },
    { month: "Jul", newCandidates: 75, deployedCandidates: 45 },
    { month: "Aug", newCandidates: 80, deployedCandidates: 50 },
    { month: "Sep", newCandidates: 85, deployedCandidates: 55 },
    { month: "Oct", newCandidates: 90, deployedCandidates: 60 },
    { month: "Nov", newCandidates: 95, deployedCandidates: 65 },
    { month: "Dec", newCandidates: 100, deployedCandidates: 70 },
  ];

  const userGrowthData = [
    { name: "Jan", revenue: 500000 }, // 5L
    { name: "Feb", revenue: 700000 }, // 7L
    { name: "Mar", revenue: 650000 }, // 6.5L
    { name: "Apr", revenue: 800000 }, // 8L
    { name: "May", revenue: 900000 }, // 9L
    { name: "Jun", revenue: 850000 }, // 8.5L
    { name: "Jul", revenue: 950000 }, // 9.5L
    { name: "Aug", revenue: 1000000 }, // 10L
    { name: "Sep", revenue: 1050000 }, // 10.5L
    { name: "Oct", revenue: 1100000 }, // 11L
    { name: "Nov", revenue: 1150000 }, // 11.5L
    { name: "Dec", revenue: 1200000 }, // 12L
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Paper
      sx={{
        padding: { xs: 2, sm: 3, md: 5 },
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mt: 3,
          textAlign: "left",
          fontFamily: "Geist, sans-serif",
          color: "#333",
          fontWeight: "600",
        }}
      >
        Admin Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2, }}>
        {[
          {
            icon: <PeopleIcon fontSize="large" sx={{ color: "#FF6F61" }} />,
            title: "Clients",
            value: `50 +`,
            color: "#bbe7fe",
          },
          {
            icon: <PersonIcon fontSize="large" sx={{ color: "#6A0572" }} />,
            title: "Candidates",
            value: `250 +`,
            color: "#d3b5e5",
          },
          {
            icon: <WorkIcon fontSize="large" sx={{ color: "#0077B6" }} />,
            title: "Staff",
            value: `30 +`,
            color: "#ffd4db",
          },
          {
            icon: <EventIcon fontSize="large" sx={{ color: "#FFC200" }} />,
            title: "Deployed Candidates",
            value: `100 + `,
            color: "#eff1db",
          },
        ].map((item, index) => (
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3} key={index}>
            <Card
              sx={{
                backgroundColor: item.color,
                boxShadow:"none",
                borderRadius: 3,
                height: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%", // Ensures it takes the full width of its parent
              }}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                  width: "100%",
                  minHeight: "175px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center", // Centers content inside CardContent
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: "1px",
                    fontWeight: "600",
                  }}
                >
                  {item.value}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    letterSpacing: "1px",
                    fontWeight: "400",
                  }}
                >
                  {item.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 4 }}>
        <Grid item xs={12} md={12} lg={12} xl={6}>
          <Box
            sx={{
              p: { xs: 1, sm: 3 },
              backgroundColor: "#fff",
              borderRadius: 3,
              border: "2px solid #E5E7EB",
              height: "450px",
              paddingBottom: { xs: "70px", sm: "30px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ flex: 7 }}>
                <Typography
                  variant="h6"
                  sx={{ fontFamily: "Geist, sans-serif", color: "#333" }}
                >
                  Candidate Registration Summary
                </Typography>
              </Box>

              <Box
                sx={{ flex: 5, display: "flex", justifyContent: "flex-end" }}
              >
                <SelectInput
                  maxWidth="250px"
                  label="Year"
                  options={[
                    { label: "2021", value: "2021" },
                    { label: "2022", value: "2022" },
                    { label: "2023", value: "2023" },
                    { label: "2024", value: "2024" },
                    { label: "2025", value: "2025" },
                  ]}
                  value={year1}
                  onChange={(e) => {
                    setYear1(e.target.value);
                  }}
                />
              </Box>
            </Box>

            <ResponsiveContainer
              width="100%"
              height="100%"
              style={{
                padding: "40px 0px",
                position: "relative",
                right: "20px",
              }}
            >
              <BarChart data={jobData}>
                <XAxis dataKey="month" stroke="#333" />
                <YAxis stroke="#333" />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="newCandidates"
                  fill="#0099D3"
                  name="New"
                  // radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="deployedCandidates"
                  fill="#E26C09"
                  name="Deployed"
                  // radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} md={12} lg={12} xl={6}>
          <Box
            sx={{
              p: { xs: 1, sm: 3 },
              backgroundColor: "#fff",
              borderRadius: 3,
              border: "2px solid #E5E7EB",
              height: "450px",
              paddingBottom: { xs: "70px", sm: "30px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ flex: 7 }}>
                <Typography
                  variant="h6"
                  sx={{ fontFamily: "Geist, sans-serif", color: "#333" }}
                >
                  Revenue Information (in Lakhs)
                </Typography>
              </Box>

              <Box
                sx={{ flex: 5, display: "flex", justifyContent: "flex-end" }}
              >
                <SelectInput
                  maxWidth="250px"
                  label="Year"
                  options={[
                    { label: "2021", value: "2021" },
                    { label: "2022", value: "2022" },
                    { label: "2023", value: "2023" },
                    { label: "2024", value: "2024" },
                    { label: "2025", value: "2025" },
                  ]}
                  onChange={(e) => {
                    setYear2(e.target.value);
                  }}
                  value={year2}
                  // placeholder="2025"
                />
              </Box>
            </Box>

            <ResponsiveContainer
              width="100%"
              height="100%"
              style={{
                padding: "40px 0px",
                position: "relative",
                right: "20px",
              }}
            >
              <LineChart data={userGrowthData}>
                <XAxis dataKey="name" stroke="#333" />
                <YAxis
                  stroke="#333"
                  tickFormatter={(value) => `${value / 100000}L`}
                />
                <Tooltip formatter={(value) => `${value / 100000} L`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="rgb(138, 43, 226)"
                  strokeWidth={3}
                  name="Revenue (Lakhs)"
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Dashboard;
